<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useReferenceUnitStore } from '../../stores/referenceUnitStore';
import { useMatterStore } from '../../stores/matterStore';
import { getComponentsAsync } from './componentRegistry';

const props = defineProps<{
    typeName: string;
    referencesData: {
        references: Record<string, unknown[]>;
        answer?: string;
        question?: string;
    };
}>();

const { t } = useI18n();

const referenceUnitStore = useReferenceUnitStore();
const matterStore = useMatterStore();

const selectedReferenceUnit = computed(() =>
    referenceUnitStore.getSelectedReferenceUnit(props.typeName),
);

const references = computed(() => props.referencesData.references[props.typeName] || []);

const componentToRender = shallowRef<unknown | null>(null);
const loading = ref(true);

async function loadComponent() {
    loading.value = true;
    componentToRender.value = null;

    const components = await getComponentsAsync(
        props.typeName,
        selectedReferenceUnit.value,
        matterStore.currentMatter?.RecordId ?? null,
    );

    componentToRender.value = components?.referenceView || null;
    loading.value = false;
}

watch([() => props.typeName, selectedReferenceUnit], loadComponent, { immediate: true });

const question = computed(() => props.referencesData.question || '');
</script>

<template>
    <div class="reference-view-container">
        <div class="reference-header">
            <h3 v-if="references.length > 0" class="reference-count">
                {{ t('references.referencesFound', references.length) }}
            </h3>
        </div>

        <div v-if="loading" class="loading-state">
            <span class="loading-spinner"></span>
        </div>

        <component
            :is="componentToRender"
            v-else-if="componentToRender"
            :references="references"
            :question="question"
        />

        <div v-else class="no-component-warning">
            <p>{{ t('references.noViewAvailable', { type: typeName, view: selectedReferenceUnit }) }}</p>
            <p class="hint">{{ t('references.componentKey', { key: `${typeName}-${selectedReferenceUnit}` }) }}</p>
        </div>
    </div>
</template>
